
import { Button } from '@/components/ui/button';
import { Play, Pause, Upload, Loader2, RotateCcw, Check } from 'lucide-react';
import { useReferentBrandingColors } from '@/hooks/useReferentBrandingColors';
import { BrandedBackground, BrandedText } from '@/components/shared/ui';

interface BrandingSettings {
  enabled: boolean;
  logo_url?: string;
  primary_color?: string;
}

interface PlaybackStateProps {
  audioUrl: string;
  recordingTime: number;
  isPlaying: boolean;
  isUploading: boolean;
  isUploadSuccess: boolean;
  audioRef: React.RefObject<HTMLAudioElement>;
  onPlay: () => void;
  onPause: () => void;
  onReset: () => void;
  onUpload: () => void;
  onPlayingEnd: () => void;
  branding?: BrandingSettings | null;
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const PlaybackState = ({
  audioUrl,
  recordingTime,
  isPlaying,
  isUploading,
  isUploadSuccess,
  audioRef,
  onPlay,
  onPause,
  onReset,
  onUpload,
  onPlayingEnd,
  branding
}: PlaybackStateProps) => {
  const colors = useReferentBrandingColors(branding);
  return (
    <div className="space-y-6 font-['Bricolage_Grotesque']">
      {/* Timer principal en couleur branding */}
      <div className="text-center">
        <div 
          className="text-6xl font-light tracking-wider mb-2"
          style={{ color: colors.primary }}
        >
          {formatTime(recordingTime)}
        </div>
        <div className="text-gray-700 text-lg font-medium">
          Enregistrement terminé
        </div>
      </div>
      
      <audio
        ref={audioRef}
        src={audioUrl}
        onEnded={onPlayingEnd}
        className="hidden"
      />
      
      {/* Boutons côte à côte - Responsive */}
      <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
        <Button 
          onClick={isPlaying ? onPause : onPlay} 
          variant="outline"
          className="flex items-center justify-center gap-2 px-6 py-3 bg-white border-gray-200 hover:bg-gray-50"
        >
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors.primary }}
          >
            {isPlaying ? (
              <Pause className="h-4 w-4 text-white" />
            ) : (
              <Play className="h-4 w-4 text-white ml-0.5" />
            )}
          </div>
          <span className="text-gray-700 font-medium">
            {isPlaying ? 'Pause' : 'Écouter'}
          </span>
        </Button>
        
        <Button 
          onClick={onReset} 
          variant="outline"
          className="flex items-center justify-center gap-2 px-6 py-3 bg-white border-gray-200 hover:bg-gray-50"
        >
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#F59E0B' }}
          >
            <RotateCcw className="h-4 w-4 text-white" />
          </div>
          <span className="text-gray-700 font-medium">Recommencer</span>
        </Button>
      </div>

      <BrandedBackground
        branding={branding}
        opacity={0.1}
        className="p-4 rounded-lg"
      >
        <BrandedText 
          className="text-sm text-center"
          branding={branding}
          as="p"
        >
          Votre enregistrement sera automatiquement transcrit et ajouté à votre évaluation.
        </BrandedText>
      </BrandedBackground>

      <Button 
        onClick={onUpload} 
        disabled={isUploading || isUploadSuccess}
        size="lg"
        className="w-full py-4"
        style={{
          backgroundColor: isUploadSuccess ? '#10B981' : colors.primary,
          borderColor: isUploadSuccess ? '#10B981' : colors.primary
        }}
      >
        {isUploadSuccess ? (
          <>
            <Check className="h-5 w-5 mr-2" />
            ✅ Audio uploadé avec succès
          </>
        ) : isUploading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Upload et transcription en cours...
          </>
        ) : (
          <>
            <Upload className="h-5 w-5 mr-2" />
            Valider et transcrire
          </>
        )}
      </Button>
    </div>
  );
};
